import { defineTable } from "convex/server";
import { v, type Infer } from "convex/values";
import {
  internalControl,
  defineTableWithInternalControl,
} from "../../../lib/shared/validators";
import { customFieldEntityType } from "../../custom_fields";

// ============================================================================
// AUTOMATIZACIONES DE COLECCIONES (estilo "automations" de Notion)
// ============================================================================
//
// Cada automatización pertenece a una colección propia y tiene:
//   - un DISPARADOR: registro creado, registro actualizado, campo editado o
//     "llega la fecha" de un campo fecha (con desfase en días y hora);
//   - CONDICIONES opcionales sobre los campos (todas deben cumplirse);
//   - ACCIONES: notificar usuarios de la organización (in-app/correo), correos
//     fijos, correos de clientes (relación a clientes o campo correo) o webhook.
//
// La acción «Cambiar valores» modifica el registro que la disparó: esa
// edición corre las automatizaciones de nuevo con una profundidad mayor y se
// corta al llegar a `MAX_TRIGGER_DEPTH` (igual que scripts y triggers).
//
// Permisos: configurarlas exige el módulo `custom_fields` (igual que las
// colecciones). Se ejecutan como sistema dentro de la mutation que guarda el
// registro (o del scheduler, para fechas). Ver engine.ts.
//
// ============================================================================
// FASE 2: tabla polimórfica `entity_automations`. La tabla histórica
// `collection_automations` se conserva en Fase 1 (la siguen usando los motores
// ajenos — `custom_fields/mutations.ts`, `convex/lib/config/modules.ts`,
// `convex/lib/shared/delete_policies.ts` — y los tests la leen directamente).
// La nueva tabla `entity_automations` admite dos tipos de "owner":
//   - `kind: "collection"` → apunta a una colección propia (mismo dueño que hoy).
//   - `kind: "entity"`     → apunta a una tabla del sistema (`clients` o
//                            `services`) que ya tiene campos personalizados.
// La API nueva (`createEntityAutomation`, etc.) opera sobre esta tabla; la API
// histórica sigue igual hasta que la Fase 2 la remplace y borre la tabla vieja.
// ============================================================================

export const automationConditionOperator = v.union(
  v.literal("is_set_to"),
  v.literal("is_not_set_to"),
  v.literal("contains"),
  v.literal("not_contains"),
  v.literal("starts_with"),
  v.literal("ends_with"),
  v.literal("is_edited"),
  v.literal("is_cleared"),
  // Operadores sobre tipos multi-valor (`multiselect`). Aceptan una lista de
  // valores separados por coma en `condition.value`. Son excluyentes con los
  // de texto (`contains`/`starts_with`/etc., que ya cubren `select` single).
  v.literal("is_any_of"), // el campo incluye al menos uno de los valores
  v.literal("is_none_of"), // el campo no incluye ninguno de los valores
  v.literal("is_all_of"), // el campo incluye todos los valores
  v.literal("is_empty_set"), // el campo está vacío / sin valor
  v.literal("is_not_empty_set"), // el campo tiene al menos un valor
  // Operadores numéricos (`number`, `currency`).
  v.literal("gt"), // value: número, >
  v.literal("gte"), // value: número, >=
  v.literal("lt"), // value: número, <
  v.literal("lte"), // value: número, <=
  v.literal("between"), // value: "min,max" ambos números
  // Operadores de fecha (`date`).
  v.literal("before"), // value: timestamp o ISO
  v.literal("after"), // value: timestamp o ISO
  v.literal("between_dates"), // value: "from,to" en timestamp/ISO
  // Operadores de checkbox (`checkbox`).
  v.literal("is_checked"),
  v.literal("is_unchecked"),
);
export type AutomationConditionOperator = Infer<typeof automationConditionOperator>;

export const automationCondition = v.object({
  fieldKey: v.string(),
  operator: automationConditionOperator,
  /**
   * Valor a comparar (mismo formato de texto que el editor de campos).
   * Forma por operador:
   *  - `is_set_to`/`is_not_set_to`/`contains`/... → un único string.
   *  - `is_any_of`/`is_none_of`/`is_all_of` → lista CSV ("A,B").
   *  - `between` → "min,max" (ambos parseables a número).
   *  - `between_dates` → "from,to" (ambos parseables a timestamp/ISO).
   */
  value: v.optional(v.string()),
});
export type AutomationCondition = Infer<typeof automationCondition>;

export const automationTrigger = v.union(
  v.object({ type: v.literal("record_created") }),
  v.object({ type: v.literal("record_updated") }),
  /** Se crea una versión nueva del registro (colecciones con versionado). */
  v.object({ type: v.literal("version_created") }),
  /** Se edita alguno de estos campos (solo en actualizaciones). */
  v.object({ type: v.literal("field_edited"), fieldKeys: v.array(v.string()) }),
  /**
   * Llega la fecha de un campo fecha: a las `time` (HH:mm, en la zona de la
   * automatización) del día `fecha + offsetDays` (negativo = días antes).
   */
  v.object({
    type: v.literal("date_reached"),
    fieldKey: v.string(),
    offsetDays: v.number(),
    time: v.string(),
  }),
);
export type AutomationTrigger = Infer<typeof automationTrigger>;
export type AutomationTriggerType = AutomationTrigger["type"];

export const automationAction = v.union(
  /** Usuarios de la organización: notificación in-app y/o correo. */
  v.object({
    type: v.literal("notify_users"),
    userIds: v.array(v.id("users")),
    inApp: v.boolean(),
    email: v.boolean(),
  }),
  /** Correos fijos. */
  v.object({ type: v.literal("send_email"), emails: v.array(v.string()) }),
  /**
   * Correos tomados del registro: campos de relación a clientes (el correo
   * del cliente) o campos de tipo correo.
   */
  v.object({ type: v.literal("email_clients"), fieldKeys: v.array(v.string()) }),
  /** POST JSON a una URL https, firmado con el secreto de la automatización. */
  v.object({ type: v.literal("webhook"), url: v.string() }),
  /** Solicitud de "Acciones públicas" del mismo dueño, sobre el registro. */
  v.object({ type: v.literal("send_request"), definitionId: v.id("collection_action_definitions") }),
  /**
   * Cambia campos del registro que disparó la automatización. `value` es
   * texto (mismo formato que las condiciones y los valores por defecto):
   * admite `{{campo}}` del registro y `@today`/`@now` en fechas; vacío
   * vacía el campo. Se escribe por el camino normal de los registros
   * (validaciones, estados, sello, numeración, historial). Solo colecciones.
   */
  v.object({
    type: v.literal("set_values"),
    values: v.array(v.object({ fieldKey: v.string(), value: v.string() })),
  }),
  /** Inicia un flujo de la misma coleccion sobre el registro. Solo colecciones. */
  v.object({ type: v.literal("start_flow"), flowId: v.id("flows") }),
);
export type AutomationAction = Infer<typeof automationAction>;

export const automationRunStatus = v.union(v.literal("success"), v.literal("error"));

export const validatorCollectionAutomation = v.object({
  collectionId: v.id("collections"),
  name: v.string(),
  enabled: v.boolean(),
  trigger: automationTrigger,
  /** Todas deben cumplirse (AND). */
  conditions: v.array(automationCondition),
  actions: v.array(automationAction),
  /** Plantillas con variables `{{key}}`, `{{coleccion}}`, `{{usuario}}`. */
  title: v.string(),
  message: v.optional(v.string()),
  /**
   * Plantilla de correo de la coleccion para los correos de esta
   * automatizacion. Sin ella se usa la por defecto de la coleccion (si hay).
   * Solo para automatizaciones de colecciones.
   */
  emailTemplateId: v.optional(v.id("email_templates")),
  /** Zona horaria IANA: hora de "llega la fecha" y fechas en los mensajes. */
  timezone: v.string(),
  /** Secreto HMAC para firmar los webhooks (`X-Heskala-Signature`). */
  signingSecret: v.string(),
  order: v.number(),
  // Última ejecución (para el panel).
  runCount: v.optional(v.number()),
  lastRunAt: v.optional(v.number()),
  lastRunStatus: v.optional(automationRunStatus),
  lastRunError: v.optional(v.string()),
  internalControl,
});

export const collection_automations = defineTableWithInternalControl(
  validatorCollectionAutomation,
).index("by_company_and_collection", [
  "internalControl.companyId",
  "collectionId",
]);

/**
 * Ejecuciones programadas de automatizaciones "llega la fecha": una fila por
 * (automatización, registro) con la función programada, para reprogramarla o
 * cancelarla cuando cambia la fecha o la automatización.
 */
export const validatorCollectionAutomationSchedule = v.object({
  companyId: v.id("companies"),
  automationId: v.id("collection_automations"),
  recordId: v.id("collection_records"),
  runAt: v.number(),
  scheduledFunctionId: v.id("_scheduled_functions"),
});

/** Tabla interna (sin internalControl): solo la tocan el motor y el scheduler. */
export const collection_automation_schedules = defineTable(
  validatorCollectionAutomationSchedule,
).index("by_automation_and_record", ["automationId", "recordId"]);

// ────────────────────────────────────────────────────────────────────────────
// FASE 2: polimórfico. El "owner" de una automatización puede ser una colección
// propia (idéntico a hoy) o una tabla del sistema que ya admite campos
// personalizados. En Fase 1 la API histórica sigue leyendo/escribiendo la tabla
// antigua (`collection_automations`); esta tabla nueva se llena con la migración
// de arranque y con la API nueva (`createEntityAutomation`).
// ────────────────────────────────────────────────────────────────────────────

/**
 * Dueño de una automatización. Discrinado por `kind`:
 *   - `"collection"`: la automatización vigila registros de una colección propia.
 *   - `"entity"`:     la automatización vigila registros de una tabla del sistema
 *                     que admite campos personalizados (clients, services).
 */
export const automationOwner = v.union(
  v.object({ kind: v.literal("collection"), collectionId: v.id("collections") }),
  // `entityType`: clients/services/quotations (catalogo curado + campos
  // personalizados) o cualquier tabla del registro de scripting (facturas,
  // ventas...; campos derivados del schema). Lo valida `createEntityAutomation`.
  v.object({ kind: v.literal("entity"), entityType: v.string() }),
);
export type AutomationOwner = Infer<typeof automationOwner>;

/** Forma polimórfica de una automatización (Fase 2). */
export const validatorEntityAutomation = v.object({
  owner: automationOwner,
  name: v.string(),
  enabled: v.boolean(),
  trigger: automationTrigger,
  conditions: v.array(automationCondition),
  actions: v.array(automationAction),
  title: v.string(),
  message: v.optional(v.string()),
  /**
   * Plantilla de correo de la coleccion para los correos de esta
   * automatizacion. Sin ella se usa la por defecto de la coleccion (si hay).
   * Solo para automatizaciones de colecciones.
   */
  emailTemplateId: v.optional(v.id("email_templates")),
  timezone: v.string(),
  signingSecret: v.string(),
  order: v.number(),
  runCount: v.optional(v.number()),
  lastRunAt: v.optional(v.number()),
  lastRunStatus: v.optional(automationRunStatus),
  lastRunError: v.optional(v.string()),
  /**
   * ID de la fila equivalente en la tabla histórica `collection_automations`,
   * para que las funciones "viejas" (`createAutomation`, `getCollectionAutomations`,
   * etc.) puedan seguir aceptando `Id<"collection_automations">` después de que
   * los datos se hayan migrado a esta tabla. En automaciones creadas por la API
   * nueva contra esta tabla no se setea (es opcional). Fase 2 lo aprovecha para
   * eliminar la tabla histórica cuando ningún código la lea.
   */
  legacyCollectionAutomationId: v.optional(v.id("collection_automations")),
  internalControl,
});

/**
 * Tabla polimórfica. Dos índices por tipo de owner porque Convex no indexa
 * discriminadores: el motor hace `withIndex(...)` con el prefijo que toque.
 */
export const entity_automations = defineTableWithInternalControl(
  validatorEntityAutomation,
)
  .index("by_company_and_collection_owner", [
    "internalControl.companyId",
    "owner.kind",
    "owner.collectionId",
  ])
  .index("by_company_and_entity_owner", [
    "internalControl.companyId",
    "owner.kind",
    "owner.entityType",
  ])
  .index("by_legacy_collection_automation", ["legacyCollectionAutomationId"]);

/**
 * Ejecuciones programadas de automatizaciones (Fase 2). Misma forma que la
 * histórica pero el "registro dueño" se guarda como `recordTable` + `recordId`
 * (string), para que en Fase 2 pueda apuntar a `clients` o `services` sin
 * migrar el esquema. En Fase 1 siempre es `collection_records`.
 */
export const validatorEntityAutomationSchedule = v.object({
  companyId: v.id("companies"),
  automationId: v.id("entity_automations"),
  recordTable: v.string(),
  recordId: v.string(),
  runAt: v.number(),
  scheduledFunctionId: v.id("_scheduled_functions"),
});

/** Tabla interna (sin internalControl): solo la tocan el motor y el scheduler. */
export const entity_automation_schedules = defineTable(
  validatorEntityAutomationSchedule,
).index("by_automation_and_record", ["automationId", "recordId", "recordTable"]);
